import { DurationType } from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { useSubscriberPauseFormik } from "@modules/programs/programSubscriber/formik/useSubscriberPauseFormik"
import { useProgramSubscriberActions } from "@modules/programs/programSubscriber/hooks/useProgramSubscriber"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { LoggerType } from "@modules/utils/loggerUtils"
import dayjs from "dayjs"
import { ChevronLeft } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTheme } from "styled-components"

type ProgramSubscriberPauseFormProps = {
  subscriberDetails: ProgramSubscriberEntity | ProgramBundleSubscriberEntity
  moveToSubscriberDetails: () => void
  programId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramSubscriberPauseForm ({
  subscriberDetails,
  moveToSubscriberDetails,
  programId,
  className,
  ...props
}:ProgramSubscriberPauseFormProps )  {
  const defautlClassName= "flex-grow-1 flex flex-column gap-5 p-2 pt-1"
  const theme = useTheme()
  const toast = useToast()
  const language = useLanguage()

  const [isDisableMonths, setIsDisableMonths] = useState(false)
  const [isResumeVisible, setIsResumeVisible] = useState(false)
  const { resumeSubscription, isResumeSubscriptionLoading } = useProgramSubscriberActions(programId, subscriberDetails)
  const { formik, isSubmitting } = useSubscriberPauseFormik(programId, subscriberDetails, moveToSubscriberDetails)

  const suspensionTypesOptions = useMemo(() => {
    return [
      { value: DurationType.MONTHLY, label: word("box.member.table.offer.pause.monthlyInputLabel") },
      { value: DurationType.UNLIMITED, label: word("box.member.table.offer.pause.unlimitedInputLabel") },
    ]
  }, [language])

  const handleOnResume = async () => {
    await resumeSubscription()
      .then(() => {
        toast.show({
          severity: "success",
          detail: word("program.subscribers.subscriptionDetails.resume.success"),
        })
        setIsResumeVisible(false)
        moveToSubscriberDetails()
      })
      .catch((e: any) => {
        console.error(LoggerType.Service + "resume subscription", e)
        toast.show({ severity: "error", summary: word("box.member.table.offer.pause.toastResumeError") })
      })
  }

  useEffect(() => {
    if (formik.values.durationType === DurationType.UNLIMITED) {
      setIsDisableMonths(true)
      formik.setFieldValue("months", null)
    } else {
      formik.setFieldValue("months", 1)
      setIsDisableMonths(false)
    }
  }, [formik.values.durationType])

  useEffect(() => {
    if (subscriberDetails) {
      const { durationType, months } = subscriberDetails.getPause()
      formik.setFieldValue("durationType", durationType)
      formik.setFieldValue("months", months)

      if (subscriberDetails.pause) setIsResumeVisible(true)
    }
  }, [subscriberDetails])

  const durationText = useMemo(() => {
    if (subscriberDetails.pause?.resumeAt) {
      return {
        title: word("box.member.table.offer.resume.defineDuration", {
          count: dayjs(subscriberDetails.pause.resumeAt)
            .add(1, "day")
            .diff(dayjs(subscriberDetails.pause.pausedAt), "months"),
        }),
        subtitle: `${dayjs(subscriberDetails.pause.pausedAt).format("ll")} ${word("global.periodInterval.to")}${" "}${dayjs(
          subscriberDetails.pause.resumeAt,
        ).format("ll")}`,
      }
    } else if (subscriberDetails.pause) {
      return {
        title: word("box.member.table.offer.resume.unlimitedDuration"),
        subtitle: `  ${word("box.member.table.offer.resume.unlimitedDurationSince")} ${dayjs(
          subscriberDetails.pause.pausedAt,
        ).format("LL")}`,
      }
    }
    return { title: "", subtitle: "" }
  }, [subscriberDetails])

  return (
    <div className={className ?? defautlClassName} {...props}>
      {/* Form header */}
      <div className="flex flex-column gap-3 align-items-center">
        <div className="flex flex-row justify-content-center w-full relative">
          <HUButton
            type="Left_Icon"
            size="XS"
            colorType="Underline"
            text={word("global.return")}
            icon={{ iconView: <ChevronLeft color={theme.neutralColor600} size={13} /> }}
            onClick={moveToSubscriberDetails}
            textColor={theme.neutralColor600}
            className="absolute left-0"
          />

          <HUText fontStyle="BL" className="text-center">
            {word("program.subscribers.subscriptionDetails.pause.title")}
          </HUText>
        </div>

        <HUText fontStyle="BM" className="text-center" color={theme.neutralColor600}>
          {subscriberDetails instanceof ProgramBundleSubscriberEntity
            ? word("programBundle.subscribers.subscriptionDetails.pause.description")
            : word("program.subscribers.subscriptionDetails.pause.description")}
        </HUText>
      </div>

      {/* Content */}
      {isResumeVisible ? (
        // RESUME
        <>
          <div className="flex flex-column flex-grow-1 justify-content-center gap-2">
            <HUText fontStyle="LM" color={theme.neutralColor700}>
              {word("box.member.table.offer.pause.supensionTypeLabel")}
            </HUText>
            <HUText fontStyle="TM" color={theme.neutralColor900}>
              {durationText.title}
            </HUText>
            <HUText fontStyle="LM" color={theme.neutralColor700} className="mt-2">
              {durationText.subtitle}
            </HUText>
          </div>

          <div className="flex flex-row align-items-center mt-auto gap-6">
            <HUButton
              type="Right_Icon"
              size="M"
              colorType="Primary"
              text={word("box.member.table.offer.pause.resumeButton")}
              onClick={() => handleOnResume()}
              className="w-full"
              disabled={isResumeSubscriptionLoading}
            />
          </div>
        </>
      ) : (
        // PAUSE FORM
        <>
          <div className="flex-grow-1">
            <FormikInput
              flex
              formikStyle={{ flexGrow: 1 }}
              label={word("box.member.table.offer.pause.supensionTypeLabel")}
              type="dropdown"
              getFieldProps={formik.getFieldProps}
              isInvalid={isFormFieldInValid("durationType", formik)}
              name="durationType"
              error={getFormErrorMessage("durationType", formik)}
              dropdownProps={{
                value: formik.values.durationType,
                options: suspensionTypesOptions,
              }}
              setFieldValue={formik.setFieldValue}
              disabled={isSubmitting}
            />
            <FormikInput
              flex
              formikStyle={{ flexGrow: 1 }}
              type="number"
              getFieldProps={formik.getFieldProps}
              isInvalid={isFormFieldInValid("months", formik)}
              name="months"
              error={getFormErrorMessage("months", formik)}
              numberProps={{
                min: 1,
                maxFractionDigits: 0,
              }}
              setFieldValue={formik.setFieldValue}
              disabled={isDisableMonths || isSubmitting}
              rightButton={
                <HUText fontStyle="TS">{word("global.month.label", { count: formik.values.months ?? 1 })}</HUText>
              }
            />
          </div>

          {/* Submit button */}
          <div className="flex flex-row align-items-center mt-auto gap-6">
            <HUButton
              type="Default"
              colorType="Quaternary"
              size="M"
              text={word("global.cancel")}
              className="flex-1"
              onClick={moveToSubscriberDetails}
              disabled={isSubmitting}
            />
            <HUButton
              type="Default"
              colorType="Primary"
              size="M"
              text={word("global.save")}
              className="flex-1"
              onClick={formik.submitForm}
              disabled={isSubmitting}
              loading={isSubmitting}
            />
          </div>
        </>
      )}
    </div>
  )
}
